import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Box, Table, Text } from '@factorialco/deprecated-design-system'
import {
  CreateSimpleLineItemDocument,
  QuotePricingLineItemFragment,
  QuotePricingPageDocument,
  UpdateSimpleLineItemDocument,
} from 'generated/resources/graphql'
import debounce from 'lodash/debounce'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import Header from './Header'
import Body from './_Body'

interface QuotePhasesTableProps {
  currency: string
  lineItems: QuotePricingLineItemFragment[]
  isProductQuote: boolean
  isDraftStatus: boolean
}
const QuotePhasesTable = ({
  currency,
  lineItems,
  isProductQuote,
  isDraftStatus,
}: QuotePhasesTableProps) => {
  const { quoteId } = useParams()
  const [standaloneItems, setStandaloneItems] = useState<QuotePricingLineItemFragment[]>(lineItems)

  useEffect(() => {
    setStandaloneItems(lineItems)
  }, [lineItems])
  const [createSimpleLineItem] = useMutation(CreateSimpleLineItemDocument, {
    extractorFn: (data) => data.financeMutations.createSimpleFinanceLineItem,
    refetchQueries: [QuotePricingPageDocument],
  })
  const [updateSimpleLineItem] = useMutation(UpdateSimpleLineItemDocument, {
    extractorFn: (data) => data.financeMutations.updateSimpleFinanceLineItem,
    refetchQueries: [QuotePricingPageDocument],
  })

  const updateSimpleLineItemRef = useRef(updateSimpleLineItem)
  useEffect(() => {
    updateSimpleLineItemRef.current = updateSimpleLineItem
  }, [updateSimpleLineItem])

  const standaloneItemsRef = useRef(standaloneItems)
  useEffect(() => {
    standaloneItemsRef.current = standaloneItems
  }, [standaloneItems])

  const debouncedUpdate = useMemo(
    () =>
      debounce(async (itemId: number, updates: Partial<QuotePricingLineItemFragment>) => {
        const currentItem = standaloneItemsRef.current.find((item) => item.id === itemId)
        if (!currentItem) return

        await updateSimpleLineItemRef.current({
          variables: {
            id: itemId,
            discountPercentage: 0,
            description: updates.description ?? currentItem.description ?? '-',
            quantity: updates.quantity ?? currentItem.quantity ?? 0,
            unitPriceCents: updates.unitPriceCents ?? currentItem.unitPriceCents ?? 0,
          },
        })
      }, 1000),
    []
  )

  useEffect(() => {
    return () => {
      debouncedUpdate.cancel()
    }
  }, [debouncedUpdate])

  const handleUpdateStandaloneItem = async (
    itemId: number,
    updates: Partial<QuotePricingLineItemFragment>
  ) => {
    setStandaloneItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, ...updates } : item))
    )
    debouncedUpdate(itemId, updates)
  }

  const handleAddStandaloneItem = async (itemType: 'role' | 'simple') => {
    const result = await createSimpleLineItem({
      variables: {
        description: '-',
        discountPercentage: 0,
        quantity: 1,
        sourceId: Number(quoteId),
        sourceType: 'quote',
        unitPriceCents: 0,
      },
    })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
        time: 2000,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.save_error'),
        time: 2000,
      })
    }
  }

  const handleDeleteStandaloneItem = (itemId: number) => {
    setStandaloneItems((prev) => prev.filter((item) => item.id !== itemId))
  }

  return (
    <>
      <Box
        flexDirection="row"
        justifyContent="spaceBetween"
        alignItems="center"
        paddingBottom="s20"
      >
        <Text size="400" weight="semibold" color="grey1000">
          {i18n.t('finance.quotes.quote_phases_table.title')}
        </Text>
      </Box>
      <Table autoWidth>
        <Header isDraftStatus={isDraftStatus} isProductQuote={isProductQuote} />
        <Body
          standaloneItems={standaloneItems}
          currency={currency}
          isDraftStatus={isDraftStatus}
          isProductQuote={isProductQuote}
          onUpdateStandaloneItem={handleUpdateStandaloneItem}
          onAddStandaloneItem={handleAddStandaloneItem}
          onDeleteStandaloneItem={handleDeleteStandaloneItem}
        />
      </Table>
    </>
  )
}

export default QuotePhasesTable
